import { inject, Injectable } from '@angular/core';
import { CsAgentAuthService } from '@spartacus/asm/root';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { BehaviorSubject, Observable, of, shareReplay, Subscription, switchMap, timer } from 'rxjs';
import * as config from '../shared/config/app.config';
import { BaseService } from '../shared/services/base.service';
import { filter, tap } from 'rxjs/operators';
import { SharedService } from '../shared/services/shared.service';
import { InvoicesCache, InvoicesFilterParams, OpenInvoicesApi } from '../shared/model/invoices.model';

const CACHED_INVOICES_KEY = 'notaFiscalCache';
const CACHE_INTERVAL = 900000;

@Injectable({
    providedIn: 'root',
})
export class MakeAPaymentService {
    private readonly sharedService = inject(SharedService);
    public creditInvoiceDate = new BehaviorSubject<any>(false); // behaviour subject containing the parent data of invoices & credit
    creditInvoiceDate$ = this.creditInvoiceDate.asObservable();
    public creditData = new BehaviorSubject<boolean>(false); // behaviour subject containing the credit Data
    creditData$ = this.creditData.asObservable();
    public invoicesData = new BehaviorSubject<boolean>(false); // behaviour subject containing the invoice Date
    invoicesData$ = this.invoicesData.asObservable();
    public closedInvoiceData = new BehaviorSubject<any>(false);
    closedInvoiceData$ = this.closedInvoiceData.asObservable();
    timerSub: Subscription;

    emails: any;
    emails$: any;
    private cache$: Observable<OpenInvoicesApi>;
    private cachedParams: InvoicesFilterParams = {};
    private freshLoad = true;

    constructor(
        private readonly baseService: BaseService,
        private readonly csAgentAuthService: CsAgentAuthService,
        private readonly user: UserAccountFacade
    ) {
        this.handleCacheOnUserLogout();
    }

    public getInvoices(params: InvoicesFilterParams): Observable<OpenInvoicesApi> {
        const asmEnabled = localStorage.getItem('asmEnabled');
        const asmUserId = localStorage.getItem('asmUserId');
        if (asmEnabled && asmUserId) {
            params['emulatedUser'] = asmUserId;
        }

        const newParams = JSON.stringify(params);
        if (JSON.stringify(this.cachedParams) !== newParams) {
            this.setupCacheTimer(newParams);
            this.cachedParams = params;
        }

        const cachedInvoices = this.sharedService.getSessionStorage(CACHED_INVOICES_KEY) as InvoicesCache;

        if (!this.cache$ && cachedInvoices?.timestamp) {
            this.cache$ = of(cachedInvoices.response);

            return this.cache$;
        }

        if (!this.cache$) {
            this.cache$ = this.baseService.get(config.GET_INVOICES, params)
                .pipe(
                    shareReplay(1),
                    tap(response => {
                        this.setupCache(params, response);
                    })
                );
        }

        return this.cache$;
    }

    public getInvoicesObs(params: InvoicesFilterParams): Observable<OpenInvoicesApi> {
        return this.baseService.get(config.GET_INVOICES, params);
    }

    public getClosedInvoices(params: InvoicesFilterParams): void {
        this.csAgentAuthService.isCustomerEmulated().subscribe((res) => {
            if (res) {
                this.user.get()
                    .pipe(
                        filter((userData: any) => !!userData?.userName),
                        switchMap(userData => {
                          params.emulatedUser = userData.userName;
                          return this.baseService.get(config.GET_CLOSEDINVOICES, params)
                        })
                    )
                    .subscribe(closedInvoices => {
                      this.closedInvoiceData.next(closedInvoices);
                });

                return;
            }

            this.baseService.get(config.GET_CLOSEDINVOICES, params)
                .subscribe((closedInvoices) => {
                    this.closedInvoiceData.next(closedInvoices);
                });
        });
    }

    public downloadPdf(params): void {
        this.baseService
            .getPdf(config.DOWNLOAD_INVOICE_CREDIT_PDF, { invoiceNumber: params })
            .subscribe(
                (res) => {
                    if (res) {
                        const file = new Blob([res], { type: 'application/pdf' });
                        const fileURL = URL.createObjectURL(file);
                        window.open(fileURL);
                    }
                },
                (error) => {
                }
            );
    }

    public getCardDetails(params): any {
        return this.baseService.get(config.GET_CARD_DETAILS, params);
    }

    public saveCreditDetails(userName, params) {
        return this.baseService.patch(`${config.USER_ACCOUNT}${userName}/payment-confirmation-credit`, params);
    }

    public makeCreditCardPayment(userName, params) {
        return this.baseService.post(`${config.USER_ACCOUNT}${userName}/ccinvoice-payment`, params);
    }

    public getAccountId(userName): Observable<any> {
        return this.baseService.get(`${config.USER_ACCOUNT}${userName}/b2bUnits`);
    }

    getEmails(userId, b2bUnitID): void {
        this.emails = new BehaviorSubject<any>(false);
        this.emails$ = this.emails.asObservable();
        this.baseService.get(`${config.USER_ACCOUNT}${userId}/customerEmails?b2bUnitId=${b2bUnitID}`)
            .pipe(
                filter(Boolean)
            )
            .subscribe((res) => {
                this.emails.next(res);
            });
    }

    private setupCacheTimer(params: string): void {
        this.compareParams(params);
        this.clearCache();
        this.timerSub?.unsubscribe();
        this.timerSub = timer(0, CACHE_INTERVAL).subscribe(() => {
            this.revalidateCache();
            this.clearCache();
        });
    }

    private setupCache(params: any, response: any): void {
        this.sharedService.setSessionStorage(CACHED_INVOICES_KEY, {
            params: params,
            response: response,
            timestamp: Date.now()
        } as InvoicesCache);
        this.cachedParams = params;
    }

    private compareParams(params: string) {
        const cache = this.sharedService.getSessionStorage(CACHED_INVOICES_KEY) as InvoicesCache;

        if (!cache) {
            return;
        }

        if (params !== JSON.stringify(cache.params)) {
            this.sharedService.removeSessionStorageItem(CACHED_INVOICES_KEY);
        }
    }

    private revalidateCache(): void {
        const cache = this.sharedService.getSessionStorage(CACHED_INVOICES_KEY);

        if (!cache) {
            return;
        }

        if ((Date.now() - cache.timestamp) > CACHE_INTERVAL) {
            this.sharedService.removeSessionStorageItem(CACHED_INVOICES_KEY);
        }
    }

    private clearCache(): void {
        this.cache$ = null;
        this.cachedParams = {};
    }

    private handleCacheOnUserLogout(): void {
        this.user.get()
            .pipe(
                filter(userData => !userData),
            )
            .subscribe(() => {
                if (this.freshLoad) {
                    this.freshLoad = false;
                    return;
                }

                this.sharedService.removeSessionStorageItem(CACHED_INVOICES_KEY);
                this.cachedParams = {};
                this.timerSub?.unsubscribe();
                this.cache$ = null;
                this.freshLoad = false;
            });
    }
}
